import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MdDialog, MdDialogConfig, MdDialogRef } from '@angular/material';

import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';
import { WarmUpService } from 'app/services/warmup/warmup.service';

import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';
import { EntityDetailsService } from 'app/entities/details/entity-details.service';

import { BankAccountComponent } from './bankaccount/bank-account.component';

import { BankAccount } from 'app/shared/models/bank-account.model';
import { BankInstitution } from 'app/shared/models/bank-institution.model';

import { FinanceService } from 'app/finance/finance.service';
import {AddBankStatementComponent} from './bank-statement/add-bank-statement.component';
import { ViewTransactionComponent } from './view-transaction/view-transaction.component';
import {BankStatementController} from './bank-statement/bank-statement.controller';
import {ConfirmDialogComponent} from './confirm-dialog/confirm-dialog.component';
import {BankStatement} from './bank-statement.model';
import {EditTransactionComponent} from './edit-transaction/edit-transaction.component';
import { BankStatementPreviewComponent } from '../transaction-monitoring/bank-statement-preview/bank-statement-preview.component';
import { ReanalyzeComparisonComponent } from './reanalyze-comparison/reanalyze-comparison.component';
import { MoveBankStatementDialogComponent } from './move-bank-statement-dialog/move-bank-statement-dialog.component';
import { formatDisplayDate } from 'app/shared/utils/date-format.util';

@Component({
  templateUrl: './entity-finance.component.html',
  styleUrls: ['./entity-finance.component.css']
})

// @todo include functionality to have multiple account holders per account

export class EntityFinanceComponent implements OnInit, OnDestroy {

  dialogRef: MdDialogRef<any>;
  entityIdDetails: EntityIdDetails;
  selectedData: Object;

  bankInstitutions: BankInstitution[];
  bankAccounts: BankAccount[] = [];
  showBankStatements = false;
  bankAccountSelected: BankAccount;
  bankStatementDetails: any[] = [];
  periodCoverageMonths: any[] = [];
  private pollingIntervals: Map<number, any> = new Map(); // Map of bankStatementId to interval
  private autoPushAttempted: Set<number> = new Set(); // Prevent repeated auto-push attempts


  constructor(
      private dialog: MdDialog,
      private viewContainerRef: ViewContainerRef,
      private entityDetailsService: EntityDetailsService,
      private financeService: FinanceService,
      private snackBarService: SnackBarService,
      private trackingService: TrackingService,
      private bankStatementController: BankStatementController
  ) { }

  ngOnInit() {
    this.entityIdDetails = this.entityDetailsService.getEntityIdDetails();

    if (this.entityIdDetails.entityId) {
      this.updateFinanceTab();
    } else {
      this.entityDetailsService.entityDetailsUpdated.subscribe(
        (entityDetails) => {
          this.entityIdDetails = entityDetails;
          this.updateFinanceTab();
        }
      );
    }
  }

  callSnackBarService(response: any, successMsg: string, failureMsg: string) {
    this.snackBarService.showStatus(response, successMsg, failureMsg);
  }

  deleteBankAccount(bankAccount: BankAccount) {
    if (confirm(MessageService.DELETE_BANK_ACCOUNT_ALERT)) {
        this.financeService.deleteBankAccount(bankAccount.accountId, this.entityIdDetails.entityId)
            .subscribe(response => {
                if (response.flagResult) {
                  this.updateFinanceTab();
                }
                this.trackingService
                    .emitEvent(
                    TrackingConstants.BANK_ACCOUNTS,
                    TrackingConstants.DELETE_BANK_ACCOUNT,
                    TrackingConstants.SUCCESS, 1);
                this.callSnackBarService(response,
                    MessageService.DELETE_BANK_ACCOUNT_SUCCESS,
                    MessageService.DELETE_BANK_ACCOUNT_FAILURE);
            },
            err => {
                this.trackingService
                    .emitEvent(
                    TrackingConstants.BANK_ACCOUNTS,
                    TrackingConstants.DELETE_BANK_ACCOUNT,
                    TrackingConstants.FAILURE, 0);
                this.callSnackBarService(err,
                    MessageService.DELETE_BANK_ACCOUNT_SUCCESS,
                    MessageService.DELETE_BANK_ACCOUNT_FAILURE);
            });
    }
  }

  editBankAccount(account: BankAccount, bank: BankInstitution): void {
    if (bank == null) {
      bank = this.bankInstitutions.find(value => value.bankId === account.bankInstitutionId);
    }
    const action = 'Edit';
    const config = new MdDialogConfig();
    config.disableClose = true;
    config.viewContainerRef = this.viewContainerRef;

    this.dialogRef = this.dialog.open(BankAccountComponent, config);
    this.dialogRef.componentInstance.selectedBankAccount = account;
    this.dialogRef.componentInstance.bankInstitution = bank;
    this.dialogRef.componentInstance.action = action;
    this.dialogRef.afterClosed().subscribe(response => {
        this.dialogRef = null;
        if (response) {
            this.updateFinanceTab();
        }
    });
  }

  openAddBankAccountsDialog() {
      const config = new MdDialogConfig();
      config.disableClose = true;
      config.viewContainerRef = this.viewContainerRef;

      this.dialogRef = this.dialog.open(BankAccountComponent, config);
      this.dialogRef.componentInstance.action = 'Add';
      this.dialogRef.afterClosed().subscribe(result => {
          this.dialogRef = null;
          if (result) {
              this.updateFinanceTab(); // @todo result);
          }
      });
  }

  updateFinanceTab(){
    this.financeService.getBankInstitutionsAccountsForEntity(this.entityIdDetails.entityId)
      .subscribe(bankInstitutions => {
        this.bankInstitutions = bankInstitutions;
        this.initBankAccounts();
      });
  }

  private initBankAccounts() {
    this.bankAccounts = [];
    this.bankInstitutions.forEach(value => this.bankAccounts.push(...value.bankAccounts));
  }

  onBankAccountSelected($event: any) {
    this.showBankStatements = true;
    this.bankAccountSelected = $event.data;
    this.initBankStatementDetails();
  }

  private initBankStatementDetails() {
    // Clear existing polling intervals
    this.clearAllPolling();
    
    this.financeService.getBankStatementsDetails(this.bankAccountSelected.accountId).subscribe(value => {
      this.bankStatementDetails = value.arrResult || value;
      this.bankStatementDetails.map(value1 => {
        value1.transactions = this.setJsonData(value1);
        if (!value1.jsonData) {
          value1.jsonData = {};
        }
      });
      console.log('value1 => ', value);
      
      // Start polling for processing statements
      this.startPollingForProcessingStatements();

      // Auto-push statements that have transactions but are not pushed yet
      this.autoPushMissingTransactionMonitoring();
      this.refreshPeriodCoverage();
    });
  }

  private autoPushMissingTransactionMonitoring() {
    if (!this.bankStatementDetails || !Array.isArray(this.bankStatementDetails)) {
      return;
    }

    const candidates = this.bankStatementDetails.filter((bs: any) => {
      const id = bs && bs.id;
      const hasTx = bs && bs.jsonData && bs.jsonData.transactions && bs.jsonData.transactions.length > 0;
      const notPushed = bs && bs.pushedToTransactionMonitoring === false;
      const notProcessing = !this.isProcessing(bs) && !this.isFailed(bs);
      return id && hasTx && notPushed && notProcessing && !this.autoPushAttempted.has(id);
    });

    candidates.forEach((bs: any) => {
      this.autoPushAttempted.add(bs.id);
      this.financeService.pushToTransactionMonitoring(bs.id, this.entityIdDetails.clientId).subscribe(
        () => {
          // Refresh to update pushed flag and mappings
          this.initBankStatementDetails();
        },
        (error: any) => {
          // Don't spam successes; only notify on failure
          this.snackBarService.showSimpleMsj('Auto-push to Transaction Monitoring failed for a bank statement: ' + (error.message || 'Unknown error'));
        }
      );
    });
  }
  
  private startPollingForProcessingStatements() {
    this.bankStatementDetails.forEach(statement => {
      if (this.isProcessing(statement)) {
        this.startPolling(statement.id);
      }
    });
  }
  
  private startPolling(bankStatementId: number) {
    // Clear existing interval if any
    if (this.pollingIntervals.has(bankStatementId)) {
      clearInterval(this.pollingIntervals.get(bankStatementId));
    }
    
    // Poll every 30 seconds
    const interval = setInterval(() => {
      this.checkProcessingStatus(bankStatementId);
    }, 30000); // 30 seconds
    
    this.pollingIntervals.set(bankStatementId, interval);
    
    // Also check immediately
    this.checkProcessingStatus(bankStatementId);
  }
  
  private checkProcessingStatus(bankStatementId: number) {
    this.financeService.getProcessingStatus(bankStatementId).subscribe(
      (status: any) => {
        // Find the statement in our array and update it
        const statement = this.bankStatementDetails.find(s => s.id === bankStatementId);
        if (statement) {
          statement.processingState = status.processingState;
          statement.processingStartDate = status.processingStartDate;
          
          // If processing is complete or failed, stop polling
          if (!status.processingState || status.processingState === 'failed') {
            this.stopPolling(bankStatementId);
            
            // If completed, reload the statement to get updated JSON data
            if (!status.processingState && status.hasJsonData) {
              this.initBankStatementDetails();
            }
          }
          
          // Check for timeout (15 minutes)
          if (status.processingState === 'processing' && status.processingStartDate) {
            const startTime = new Date(status.processingStartDate);
            const now = new Date();
            const elapsedMinutes = (now.getTime() - startTime.getTime()) / (1000 * 60);
            
            if (elapsedMinutes > 15) {
              // Timeout exceeded, mark as failed
              statement.processingState = 'failed';
              this.stopPolling(bankStatementId);
            }
          }
        }
      },
      error => {
        console.error('Error checking processing status:', error);
      }
    );
  }
  
  private stopPolling(bankStatementId: number) {
    if (this.pollingIntervals.has(bankStatementId)) {
      clearInterval(this.pollingIntervals.get(bankStatementId));
      this.pollingIntervals.delete(bankStatementId);
    }
  }
  
  private clearAllPolling() {
    this.pollingIntervals.forEach((interval, id) => {
      clearInterval(interval);
    });
    this.pollingIntervals.clear();
  }
  
  isProcessing(bankStatement: any): boolean {
    if (!bankStatement.processingState) {
      return false;
    }
    
    // Check for timeout
    if (bankStatement.processingState === 'processing' && bankStatement.processingStartDate) {
      const startTime = new Date(bankStatement.processingStartDate);
      const now = new Date();
      const elapsedMinutes = (now.getTime() - startTime.getTime()) / (1000 * 60);
      
      if (elapsedMinutes > 15) {
        // Timeout exceeded
        return false;
      }
    }
    
    return bankStatement.processingState === 'processing';
  }
  
  isFailed(bankStatement: any): boolean {
    if (bankStatement.processingState === 'failed') {
      return true;
    }
    
    // Check for timeout
    if (bankStatement.processingState === 'processing' && bankStatement.processingStartDate) {
      const startTime = new Date(bankStatement.processingStartDate);
      const now = new Date();
      const elapsedMinutes = (now.getTime() - startTime.getTime()) / (1000 * 60);
      
      if (elapsedMinutes > 15) {
        return true;
      }
    }
    
    return false;
  }
  
  ngOnDestroy() {
    this.clearAllPolling();
  }

  openTransactions(transactions: any){
    if (this.validateTransaction(transactions) ) {
      const dialogoRef = this.dialog.open(ViewTransactionComponent);
      dialogoRef.componentInstance.jsonData = transactions;
    }else {
      this.snackBarService.showSimpleMsj('Error in analysis, try again');
    }
  }

  validateTransaction(transactions: any): boolean {
    return transactions &&
      transactions.transactions !== '-';
  }

  reRunAnalyzeDocument(bankStatement: any) {
    // Get existing transactions from current bank statement
    const existingJsonData = bankStatement.jsonData;
    const existingTransactions = existingJsonData && existingJsonData.transactions ? existingJsonData.transactions : [];
    
    this.bankStatementController.getDocumentAnalysis(
      bankStatement.uniqueDocumentName,
      bankStatement.bankAccount.accountId, 
      bankStatement.tenantId,
      (responseRequest: any, analyzedDocument: boolean) => {
        // Check if response contains status information
        if (responseRequest && responseRequest.status) {
          if (responseRequest.status === 'processing') {
            // Processing started asynchronously, update state from backend response
            bankStatement.processingState = 'processing';
            bankStatement.processingStartDate = responseRequest.processingStartDate || Date.now();
            // Start polling for this statement
            this.startPolling(bankStatement.id);
            return;
          } else if (responseRequest.status === 'failed') {
            // Analysis failed to start
            bankStatement.processingState = 'failed';
            this.snackBarService.showSimpleMsj('Failed to start document analysis: ' + (responseRequest.message || 'Unknown error'));
            return;
          }
        }
        
        // Legacy synchronous processing or completed analysis
        if (analyzedDocument && responseRequest) {
          const newTransactions = responseRequest.transactions || [];
          
          // Check if old and new transactions are the same
          if (this.areTransactionsIdentical(existingTransactions, newTransactions)) {
            // Transactions are identical, automatically apply merge (keep all) without showing dialog
            const mergeResult = {
              toRemove: [],
              toAdd: [],
              toKeep: existingTransactions
            };
            this.applyReanalyzeMerge(bankStatement.id, mergeResult, responseRequest);
            this.snackBarService.showSimpleMsj('Re-analysis completed. No changes detected - transactions are identical.');
            return;
          }
          
          // Transactions differ, show comparison dialog
          const dialogConfig = new MdDialogConfig();
          dialogConfig.width = '90%';
          dialogConfig.data = {
            originalTransactions: existingTransactions,
            newTransactions: newTransactions,
            bankStatementId: bankStatement.id
          };
          
          const dialogRef = this.dialog.open(ReanalyzeComparisonComponent, dialogConfig);
          dialogRef.afterClosed().subscribe((result: any) => {
            if (result) {
              // Apply the merge
              this.applyReanalyzeMerge(bankStatement.id, result, responseRequest);
            }
          });
        }
      },
      bankStatement.id // Pass bankStatementId for async processing
    );
  }

  private areTransactionsIdentical(originalTransactions: any[], newTransactions: any[]): boolean {
    // Use the same key function as ReanalyzeComparisonComponent
    const getTransactionKey = (tx: any): string => {
      const date = tx.tx_date || '';
      const amount = tx.tx_amount || '';
      const description = (tx.tx_description || '').substring(0, 50);
      const counterparty = tx.tx_counter_party || '';
      return `${date}::${amount}::${description}::${counterparty}`;
    };

    // If counts differ, they're not identical
    if (originalTransactions.length !== newTransactions.length) {
      return false;
    }

    // Create sets of transaction keys
    const originalKeys = new Set(originalTransactions.map(tx => getTransactionKey(tx)));
    const newKeys = new Set(newTransactions.map(tx => getTransactionKey(tx)));

    // Check if all keys match
    if (originalKeys.size !== newKeys.size) {
      return false;
    }

    // Check if all keys in original exist in new (convert Set to array for iteration)
    const originalKeysArray = Array.from(originalKeys);
    for (let i = 0; i < originalKeysArray.length; i++) {
      if (!newKeys.has(originalKeysArray[i])) {
        return false;
      }
    }

    return true;
  }

  private applyReanalyzeMerge(bankStatementId: number, mergeResult: any, newJsonData: any) {
    // Call backend to apply the merge
    this.financeService.applyReanalyzeMerge(bankStatementId, mergeResult, newJsonData).subscribe(
      (response: any) => {
        this.snackBarService.showSimpleMsj('Bank statement reanalyzed and merged successfully');
        this.initBankStatementDetails();
      },
      error => {
        this.snackBarService.showSimpleMsj('Error applying merge: ' + (error.message || 'Unknown error'));
      }
    );
  }

  saveJsonData(data: any, bankStatementId: number) {
    this.bankStatementController.saveJsonObject(data, bankStatementId, (success: boolean) => {
      // if (success) {
        this.initBankStatementDetails();
      // }
    });
  }

  public setJsonData(value: any): any {
    return value.jsonData !== null ? value!.jsonData : '-';
  }
  public setOpeningBalance(value: any): any {
    return value !== null && value.jsonData.start_balance !== undefined && value.jsonData.start_balance !== null ? this.formatAmount(value.jsonData.start_balance) : '-';
  }

  public setClosingBalance(value: any): any {
    return value.jsonData !== null && value.jsonData.end_balance ? this.formatAmount(value.jsonData.end_balance) : '-';
  }

  public setCurrency(value: any): any {
    return value.jsonData !== null && value!.jsonData.currency ? value.jsonData.currency : '-';
  }

  public setStartDate(value: any): any {
    const date = this.getStatementPeriodStart(value);
    return date ? this.formatDateOnly(date) : '-';
  }

  public setEndDate(value: any): any {
    const date = this.getStatementPeriodEnd(value);
    return date ? this.formatDateOnly(date) : '-';
  }

  public getTransactionCount(value: any): number {
    if (value && value.txCount != null) {
      return value.txCount;
    }
    if (value && value.jsonData && value.jsonData.transactions && Array.isArray(value.jsonData.transactions)) {
      return value.jsonData.transactions.length;
    }
    return 0;
  }

  isEmptyStatement(bankStatement: any): boolean {
    return this.getTransactionCount(bankStatement) === 0;
  }

  canConfirmEmptyStatement(bankStatement: any): boolean {
    return !this.isProcessing(bankStatement)
      && !this.isFailed(bankStatement)
      && this.isEmptyStatement(bankStatement)
      && bankStatement.confirmed !== true;
  }

  confirmEmptyBankStatement(bankStatement: any) {
    if (!bankStatement || !bankStatement.id) {
      return;
    }
    this.financeService.confirmBankStatement(bankStatement.id).subscribe(
      (response: any) => {
        const result = response && response.arrResult ? response.arrResult : response;
        this.snackBarService.showSimpleMsj(
          (result && result.message) || 'Bank statement confirmed (no transactions)'
        );
        this.initBankStatementDetails();
      },
      (error: any) => {
        this.snackBarService.showSimpleMsj('Error confirming bank statement: ' + (error.message || 'Unknown error'));
      }
    );
  }

  private getStatementPeriodStart(bankStatement: any): Date | null {
    if (!bankStatement) {
      return null;
    }
    if (bankStatement.statementStartDate) {
      return new Date(bankStatement.statementStartDate);
    }
    if (bankStatement.jsonData && bankStatement.jsonData.start_date) {
      return this.parseDateString(bankStatement.jsonData.start_date);
    }
    return null;
  }

  private getStatementPeriodEnd(bankStatement: any): Date | null {
    if (!bankStatement) {
      return null;
    }
    if (bankStatement.statementEndDate) {
      return new Date(bankStatement.statementEndDate);
    }
    if (bankStatement.jsonData && bankStatement.jsonData.end_date) {
      return this.parseDateString(bankStatement.jsonData.end_date);
    }
    return null;
  }

  private parseDateString(value: string): Date | null {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatDateOnly(date: Date): string {
    return formatDisplayDate(date);
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private endOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  }

  private refreshPeriodCoverage() {
    this.periodCoverageMonths = this.buildPeriodCoverageMonths(this.bankStatementDetails || []);
  }

  private buildPeriodCoverageMonths(statements: any[]): any[] {
    const months: any[] = [];
    const now = new Date();
    const rangeStart = new Date(now.getFullYear(), now.getMonth() - 23, 1);

    const periods = statements
      .map((statement) => {
        const start = this.getStatementPeriodStart(statement);
        const end = this.getStatementPeriodEnd(statement);
        if (!start || !end) {
          return null;
        }
        return {
          start: this.startOfDay(start),
          end: this.endOfDay(end),
          confirmed: statement.confirmed === true,
          empty: this.isEmptyStatement(statement),
          name: statement.documentName || 'Statement'
        };
      })
      .filter((period) => period != null);

    const cursor = new Date(rangeStart);
    while (cursor <= now) {
      const monthStart = this.startOfDay(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
      const monthEnd = this.endOfDay(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0));
      const label = monthStart.toLocaleString('default', { month: 'short', year: '2-digit' });

      const overlapping = periods.filter((period) =>
        period.start.getTime() <= monthEnd.getTime() && period.end.getTime() >= monthStart.getTime()
      );

      let color = '#e0e0e0';
      let tooltip = `${label}: No statement on file`;

      if (overlapping.length > 0) {
        const confirmed = overlapping.filter((period) => period.confirmed);
        if (confirmed.length > 0) {
          const hasGap = this.monthHasCoverageGap(monthStart, monthEnd, confirmed);
          color = hasGap ? '#ffb74d' : '#66bb6a';
          tooltip = hasGap
            ? `${label}: Confirmed statement(s) with coverage gap`
            : `${label}: Period covered${confirmed.some((p) => p.empty) ? ' (including empty statement)' : ''}`;
        } else {
          color = '#fff176';
          tooltip = `${label}: Unconfirmed statement on file`;
        }
      }

      months.push({ label, color, tooltip });
      cursor.setMonth(cursor.getMonth() + 1);
    }

    return months;
  }

  private monthHasCoverageGap(monthStart: Date, monthEnd: Date, periods: any[]): boolean {
    let cursor = monthStart.getTime();
    const endTime = monthEnd.getTime();

    const sorted = periods.slice().sort((a, b) => a.start.getTime() - b.start.getTime());
    for (const period of sorted) {
      const overlapStart = Math.max(period.start.getTime(), monthStart.getTime());
      const overlapEnd = Math.min(period.end.getTime(), monthEnd.getTime());
      if (overlapStart > overlapEnd) {
        continue;
      }
      if (overlapStart > cursor) {
        return true;
      }
      cursor = Math.max(cursor, overlapEnd + 1);
      if (cursor > endTime) {
        return false;
      }
    }

    return cursor <= endTime;
  }

  public formatAmount(amount: any) {
    if (typeof amount !== 'number' || isNaN(amount)) {
      return amount;
    }
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  openAddBankStatement(bankAccountSelected: BankAccount) {
    const dialogRef = this.dialog.open(AddBankStatementComponent, {
      // data: bankAccountSelected
    });
    dialogRef.componentInstance.selectedBankAccount = bankAccountSelected;
    dialogRef.afterClosed().subscribe(value => {
      if (value === true) {
        this.initBankStatementDetails();
      }
    });
  }

  deleteBankStatement(bankStatement: any) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent);
    dialogRef.componentInstance.dialogMessage = 'Are you sure you want to delete this bank statement?';
    dialogRef.componentInstance.confirmationButtonLabel = 'Delete';
    dialogRef.componentInstance.onConfirmed = (isConfirmed: boolean) => {
      if (isConfirmed) {
        this.bankStatementController.deleteBankStatement(bankStatement.id, (success: boolean) => {
          if (success) {
            this.initBankStatementDetails();
          }
        });
      }
    };
  }
  editBankStatement(bankStatement: BankStatement) {
    const dialogRef = this.dialog.open(EditTransactionComponent, {
      width: '60%',
      height: '80%',
      disableClose: true,
    });
    dialogRef.componentInstance.bankStatement = bankStatement;
    dialogRef.componentInstance.onReload = (reload: boolean) => {
      if (reload) {
        this.initBankStatementDetails();
      }
    };
  }

  previewBankStatement(bankStatement: BankStatement) {
    const config = new MdDialogConfig();
    config.disableClose = false;
    config.viewContainerRef = this.viewContainerRef;
    config.width = '80%';
    config.height = '80%';
    this.dialogRef = this.dialog.open(BankStatementPreviewComponent, config);
    this.dialogRef.componentInstance.bankStatement = bankStatement;
    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = null;
    });
  }

  downloadBankStatement(bankStatement: BankStatement) {
    if (!bankStatement.uniqueDocumentName || !bankStatement.bankAccount || !bankStatement.tenantId) {
      this.snackBarService.showSimpleMsj('Missing information to download bank statement');
      return;
    }
    this.financeService.downloadBankStatement(
      bankStatement.uniqueDocumentName,
      bankStatement.bankAccount.accountId,
      bankStatement.tenantId,
      bankStatement.documentName
    ).subscribe(
      (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = bankStatement.documentName || bankStatement.uniqueDocumentName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error => {
        this.snackBarService.showSimpleMsj('Error downloading bank statement: ' + (error.message || 'Unknown error'));
      }
    );
  }

  pushToTransactionMonitoring(bankStatement: BankStatement) {
    if (!bankStatement.id) {
      this.snackBarService.showSimpleMsj('Missing bank statement ID');
      return;
    }
    
    if (!bankStatement.jsonData || !bankStatement.jsonData.transactions || bankStatement.jsonData.transactions.length === 0) {
      this.snackBarService.showSimpleMsj('No transactions found in bank statement');
      return;
    }

    this.financeService.pushToTransactionMonitoring(bankStatement.id, this.entityIdDetails.clientId).subscribe(
      (response: any) => {
        if (response && response.arrResult) {
          const result = response.arrResult;
          const message = `Successfully pushed ${result.created || 0} transactions. ${result.reassigned || 0} reassigned. ${result.skipped || 0} transactions were already pushed.`;
          this.snackBarService.showSimpleMsj(message);
          // Reload bank statement details to get updated JSON with mappings
          this.initBankStatementDetails();
        } else {
          this.snackBarService.showSimpleMsj('Successfully pushed transactions to Transaction Monitoring');
          this.initBankStatementDetails();
        }
      },
      error => {
        this.snackBarService.showSimpleMsj('Error pushing transactions: ' + (error.message || 'Unknown error'));
      }
    );
  }

  openMoveBankStatementDialog(bankStatement: BankStatement) {
    const dialogRef = this.dialog.open(MoveBankStatementDialogComponent, {
      width: '720px',
      disableClose: true
    });
    dialogRef.componentInstance.bankStatement = bankStatement;
    dialogRef.componentInstance.sourceEntityId = this.entityIdDetails
      ? this.entityIdDetails.entityId
      : null;
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result.success) {
        const moveResult = result.result || {};
        const moved = moveResult.transactionsMoved || 0;
        const cancelled = moveResult.profileMatchesCancelled || 0;
        const rulesProcessed = moveResult.rulesProcessed || 0;
        let message = 'Bank statement moved successfully.';
        if (moved > 0) {
          message += ` ${moved} transaction(s) moved`;
          if (cancelled > 0) {
            message += `, ${cancelled} profile match(es) cancelled`;
          }
          message += '.';
        }
        if (rulesProcessed > 0) {
          message += ` Transaction monitoring re-run for ${rulesProcessed} transaction(s).`;
        }
        this.snackBarService.showSimpleMsj(message);
        this.initBankStatementDetails();
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/finance/entity-finance.component.ts